// src/components/admin/ConstruireJsonRichAdmin.jsx
import React, { useState, useCallback, useRef } from 'react';
import {
    Plus, Trash2, ChevronDown, ChevronRight,
    FileText, Layers, Image, Video, Music, File, Code, Sigma,
    Save, Eye, X, Upload, AlertCircle
} from 'lucide-react';

const CONTENT_TYPES = [
    { value: 'IMAGE', label: 'Image', icon: '🖼️' },
    { value: 'PDF', label: 'Document PDF', icon: '📄' },
    { value: 'AUDIO', label: 'Audio', icon: '🎵' },
    { value: 'VIDEO', label: 'Vidéo', icon: '🎬' },
    { value: 'FORMULE', label: 'Formule mathématique', icon: '∑' },
    { value: 'TEXT_ENRICHI', label: 'Texte enrichi (HTML)', icon: '📝' },
    { value: 'CODE_INTERACTIF', label: 'Code interactif', icon: '💻' },
];

export default function ConstruireJsonRichAdmin({
    initialJson = null,
    onSave,
    onPreview,
    token,
    moduleId, // ⚠️ id RÉEL du module en base (obligatoire pour l'upload de médias)
    apiBase = '',
    // Si non fourni, calculé automatiquement vers /api/v1/admin/modules/{moduleId}/rich/media
    uploadEndpoint = null
}) {
    // Endpoint réel d'upload : priorité à la prop si fournie, sinon convention backend
    const resolvedUploadEndpoint = uploadEndpoint || (moduleId ? `${apiBase}/api/v1/admin/modules/${moduleId}/rich/media` : null);
    const [jsonData, setJsonData] = useState(() => {
        if (initialJson) {
            return typeof initialJson === 'string' ? JSON.parse(initialJson) : initialJson;
        }
        return getDefaultFormation();
    });

    const [expandedModules, setExpandedModules] = useState({});
    const [expandedLecons, setExpandedLecons] = useState({});
    const [showJsonPreview, setShowJsonPreview] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importJson, setImportJson] = useState('');
    const [importError, setImportError] = useState(null);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadContext, setUploadContext] = useState({ modId: null, leconId: null, field: null });
    const fileInputRef = useRef(null);

    function getDefaultFormation() {
        return {
            id: `form-${Date.now()}`,
            titre: 'Nouvelle formation',
            description: 'Description de la formation',
            categorie: 'Général',
            niveau: 'Intermédiaire',
            dureeEstimee: '10 heures',
            langue: 'fr',
            actif: true,
            metadata: { version: '1.0', auteur: 'Équipe Pédagogique', derniereMiseAJour: new Date().toISOString() },
            modules: [],
            FinFormation: { titreResume: 'Félicitations !', aRetenir: 'Vous avez terminé la formation avec succès.' }
        };
    }

    const generateId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).substring(2, 6)}`;

    const getContentTypeLabel = (type) => CONTENT_TYPES.find(t => t.value === type)?.label || type;
    const getContentTypeIcon = (type) => CONTENT_TYPES.find(t => t.value === type)?.icon || '📄';

    // Gestion des modules (inchangée)
    const addModule = useCallback(() => {
        const newModule = {
            id: generateId('mod'),
            ordre: jsonData.modules.length + 1,
            titre: `Module ${jsonData.modules.length + 1}`,
            description: 'Description du module',
            lecons: [],
        };
        setJsonData(prev => ({ ...prev, modules: [...prev.modules, newModule] }));
        setExpandedModules(prev => ({ ...prev, [newModule.id]: true }));
    }, [jsonData]);

    const removeModule = useCallback((modId) => {
        if (!window.confirm('Supprimer ce module ?')) return;
        setJsonData(prev => ({ ...prev, modules: prev.modules.filter(m => m.id !== modId) }));
        setExpandedModules(prev => { const newState = { ...prev }; delete newState[modId]; return newState; });
    }, []);

    const updateModule = useCallback((modId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? { ...m, [field]: value } : m)
        }));
    }, []);

    const toggleModuleExpand = useCallback((modId) => {
        setExpandedModules(prev => ({ ...prev, [modId]: !prev[modId] }));
    }, []);

    // Gestion des leçons (inchangée)
    const addLecon = useCallback((modId) => {
        const module = jsonData.modules.find(m => m.id === modId);
        if (!module) return;
        const newLecon = {
            id: generateId('lec'),
            ordre: module.lecons.length + 1,
            titre: `Leçon ${module.lecons.length + 1}`,
            typeContenu: 'IMAGE',
            contenu: { imageUrl: '', texteExplicatif: '' },
            resumeLecon: { titreResume: `Résumé de la leçon ${module.lecons.length + 1}`, aRetenir: 'Points clés...' }
        };
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? { ...m, lecons: [...m.lecons, newLecon] } : m)
        }));
        setExpandedLecons(prev => ({ ...prev, [newLecon.id]: true }));
    }, [jsonData]);

    const removeLecon = useCallback((modId, leconId) => {
        if (!window.confirm('Supprimer cette leçon ?')) return;
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? { ...m, lecons: m.lecons.filter(l => l.id !== leconId) } : m)
        }));
        setExpandedLecons(prev => { const newState = { ...prev }; delete newState[leconId]; return newState; });
    }, []);

    const updateLecon = useCallback((modId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? {
                ...m,
                lecons: m.lecons.map(l => l.id === leconId ? { ...l, [field]: value } : l)
            } : m)
        }));
    }, []);

    const updateLeconContenu = useCallback((modId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? {
                ...m,
                lecons: m.lecons.map(l => l.id === leconId ? {
                    ...l,
                    contenu: { ...l.contenu, [field]: value }
                } : l)
            } : m)
        }));
    }, []);

    const updateLeconResume = useCallback((modId, leconId, field, value) => {
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? {
                ...m,
                lecons: m.lecons.map(l => l.id === leconId ? {
                    ...l,
                    resumeLecon: { ...l.resumeLecon, [field]: value }
                } : l)
            } : m)
        }));
    }, []);

    const toggleLeconExpand = useCallback((leconId) => {
        setExpandedLecons(prev => ({ ...prev, [leconId]: !prev[leconId] }));
    }, []);

    // Changement de type de contenu (inchangé)
    const changeLeconType = useCallback((modId, leconId, newType) => {
        let defaultContenu = {};
        switch (newType) {
            case 'IMAGE': defaultContenu = { imageUrl: '', texteExplicatif: '' }; break;
            case 'PDF': defaultContenu = { pdfUrl: '', texteExplicatif: '' }; break;
            case 'AUDIO': defaultContenu = { audioUrl: '', texteExplicatif: '' }; break;
            case 'VIDEO': defaultContenu = { videoUrl: '', texteExplicatif: '' }; break;
            case 'FORMULE': defaultContenu = { formuleTexte: '', texteExplicatif: '' }; break;
            case 'TEXT_ENRICHI': defaultContenu = { corpsTexte: '', texteExplicatif: '' }; break;
            case 'CODE_INTERACTIF': defaultContenu = { snippetCode: '', langage: 'javascript', instructions: '', texteExplicatif: '' }; break;
            default: defaultContenu = {};
        }
        setJsonData(prev => ({
            ...prev,
            modules: prev.modules.map(m => m.id === modId ? {
                ...m,
                lecons: m.lecons.map(l => l.id === leconId ? { ...l, typeContenu: newType, contenu: defaultContenu } : l)
            } : m)
        }));
    }, []);

    // Upload de fichier (modifié)
    const handleFileUpload = useCallback(async (modId, leconId, field, file) => {
        if (!file) return;

        // ⚠️ Le module doit exister en base avant de pouvoir y attacher des médias
        // (l'endpoint /rich/media a besoin de l'id réel pour créer le dossier de stockage).
        if (!moduleId) {
            setError("Enregistrez d'abord le module (bouton \"💾 Sauvegarder\") avant d'ajouter des fichiers.");
            return;
        }
        if (!resolvedUploadEndpoint) {
            setError("Endpoint d'upload introuvable (aucun moduleId fourni).");
            return;
        }

        const formData = new FormData();
        formData.append('file', file);
        formData.append('leconId', leconId); // conservé pour logs côté serveur

        setUploading(true);
        try {
            const response = await fetch(resolvedUploadEndpoint, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (!response.ok) throw new Error(`Échec de l'upload (HTTP ${response.status})`);
            const result = await response.json();
            const url = result.url || result.fileUrl || '';
            updateLeconContenu(modId, leconId, field, url);
            setSuccess('Fichier uploadé avec succès !');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Erreur upload: ' + err.message);
        } finally {
            setUploading(false);
            // Réinitialiser le file input
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    }, [token, moduleId, resolvedUploadEndpoint, updateLeconContenu]);

    // Gestion du file input avec contexte
    const handleFileInputChange = useCallback((e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const { modId, leconId, field } = uploadContext;
        if (!modId || !leconId || !field) return;
        handleFileUpload(modId, leconId, field, file);
    }, [uploadContext, handleFileUpload]);

    // Formulaire de contenu (modifié pour utiliser le contexte d'upload)
    const renderLeconContentForm = useCallback((modId, leconId, typeContenu, contenu) => {
        const renderUploadButton = (field, accept) => (
            <div className="flex items-center gap-2">
                <input
                    type="text"
                    value={contenu[field] || ''}
                    onChange={(e) => updateLeconContenu(modId, leconId, field, e.target.value)}
                    className="flex-1 px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                    placeholder="/rich-media/..."
                />
                <input
                    type="file"
                    accept={accept}
                    ref={fileInputRef}
                    className="hidden"
                    onChange={handleFileInputChange}
                />
                <button
                    onClick={() => {
                        setUploadContext({ modId, leconId, field });
                        fileInputRef.current?.click();
                    }}
                    disabled={uploading || !moduleId}
                    title={!moduleId ? "Enregistrez d'abord le module pour activer l'upload" : ''}
                    className="px-3 py-1.5 text-sm bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition disabled:opacity-50 flex items-center gap-1"
                >
                    <Upload size={16} /> {uploading ? '...' : 'Upload'}
                </button>
            </div>
        );

        switch (typeContenu) {
            case 'IMAGE':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Image</label>
                            {renderUploadButton('imageUrl', 'image/*')}
                            {contenu.imageUrl && <img src={apiBase + contenu.imageUrl} alt="aperçu" className="max-h-40 rounded border" />}
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'PDF':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Fichier PDF</label>
                            {renderUploadButton('pdfUrl', '.pdf')}
                            {contenu.pdfUrl && (
                                <a href={apiBase + contenu.pdfUrl} target="_blank" rel="noreferrer" className="inline-block mt-1 text-xs text-indigo-600 underline">
                                    📄 Voir le PDF
                                </a>
                            )}
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'AUDIO':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Fichier audio (MP3)</label>
                            {renderUploadButton('audioUrl', 'audio/*')}
                            {contenu.audioUrl && <audio controls src={apiBase + contenu.audioUrl} className="mt-1 w-full max-w-xs" />}
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'VIDEO':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Fichier vidéo (MP4)</label>
                            {renderUploadButton('videoUrl', 'video/*')}
                            {contenu.videoUrl && <video controls src={apiBase + contenu.videoUrl} className="mt-1 max-h-40 rounded border" />}
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'FORMULE':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Formule (LaTeX)</label>
                            <textarea
                                value={contenu.formuleTexte || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'formuleTexte', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                                placeholder="E = mc^2"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'TEXT_ENRICHI':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte enrichi (HTML)</label>
                            <textarea
                                value={contenu.corpsTexte || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'corpsTexte', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="6"
                                placeholder="<h1>Titre</h1><p>...</p>"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif (optionnel)</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            case 'CODE_INTERACTIF':
                return (
                    <div className="space-y-2">
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Code source</label>
                            <textarea
                                value={contenu.snippetCode || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'snippetCode', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="6"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Langage</label>
                            <input
                                type="text"
                                value={contenu.langage || 'javascript'}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'langage', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Instructions</label>
                            <textarea
                                value={contenu.instructions || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'instructions', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                        <div>
                            <label className="block text-xs text-indigo-700 font-serif">Texte explicatif (optionnel)</label>
                            <textarea
                                value={contenu.texteExplicatif || ''}
                                onChange={(e) => updateLeconContenu(modId, leconId, 'texteExplicatif', e.target.value)}
                                className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                                rows="2"
                            />
                        </div>
                    </div>
                );

            default:
                return <div className="text-amber-600">Type de contenu non supporté</div>;
        }
    }, [apiBase, moduleId, updateLeconContenu, uploading, handleFileInputChange, uploadContext]);

    // Fin de formation (inchangé)
    const updateFinFormation = useCallback((field, value) => {
        setJsonData(prev => ({ ...prev, FinFormation: { ...prev.FinFormation, [field]: value } }));
    }, []);

    // Validation (inchangé)
    const validateFormation = useCallback(() => {
        const errors = [];
        if (!jsonData.titre || jsonData.titre.trim() === '') errors.push('Titre de formation requis');
        if (!jsonData.modules || jsonData.modules.length === 0) errors.push('Au moins un module');
        jsonData.modules.forEach((mod, idx) => {
            if (!mod.titre) errors.push(`Module ${idx + 1} : titre requis`);
            if (!mod.lecons || mod.lecons.length === 0) errors.push(`Module "${mod.titre}" : au moins une leçon`);
            mod.lecons.forEach((lec, li) => {
                if (!lec.titre) errors.push(`Leçon ${li + 1} du module "${mod.titre}" : titre requis`);
                const contenu = lec.contenu || {};
                switch (lec.typeContenu) {
                    case 'IMAGE': if (!contenu.imageUrl) errors.push(`Leçon "${lec.titre}" : image requise`); break;
                    case 'PDF': if (!contenu.pdfUrl) errors.push(`Leçon "${lec.titre}" : PDF requis`); break;
                    case 'AUDIO': if (!contenu.audioUrl) errors.push(`Leçon "${lec.titre}" : audio requis`); break;
                    case 'VIDEO': if (!contenu.videoUrl) errors.push(`Leçon "${lec.titre}" : vidéo requise`); break;
                    case 'FORMULE': if (!contenu.formuleTexte) errors.push(`Leçon "${lec.titre}" : formule requise`); break;
                    case 'TEXT_ENRICHI': if (!contenu.corpsTexte) errors.push(`Leçon "${lec.titre}" : texte requis`); break;
                    case 'CODE_INTERACTIF': if (!contenu.snippetCode) errors.push(`Leçon "${lec.titre}" : code requis`); break;
                    default: errors.push(`Leçon "${lec.titre}" : type de contenu non reconnu`);
                }
            });
        });
        return errors;
    }, [jsonData]);

    // Sauvegarde (inchangé)
    const handleSave = useCallback(() => {
        const errors = validateFormation();
        if (errors.length > 0) { setError(errors.join('\n')); alert(errors.join('\n')); return; }
        setLoading(true);
        setError(null);
        setSuccess(null);
        const jsonString = JSON.stringify(jsonData, null, 2);
        if (onSave) {
            onSave(jsonString, jsonData);
            setLoading(false);
        } else if (token && moduleId) {
            // typeModule=JSON_RICH : distingue ce module d'un module JSON "simple" côté backend
            fetch(`${apiBase}/api/v1/admin/modules/${moduleId}/json-content?typeModule=JSON_RICH`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: jsonString
            })
                .then(res => { if (!res.ok) throw new Error('Erreur HTTP ' + res.status); setSuccess('✅ Sauvegardé (type JSON_RICH)'); })
                .catch(err => setError('❌ ' + err.message))
                .finally(() => setLoading(false));
        } else {
            setError('❌ Aucun mécanisme de sauvegarde');
            setLoading(false);
        }
    }, [jsonData, onSave, token, moduleId, apiBase, validateFormation]);

    // Prévisualisation (inchangé)
    const handlePreview = useCallback(() => {
        const errors = validateFormation();
        if (errors.length > 0) { setError(errors.join('\n')); return; }
        if (onPreview) onPreview(jsonData);
    }, [jsonData, onPreview, validateFormation]);

    // Export / Import (inchangé)
    const handleExportJson = () => {
        const blob = new Blob([JSON.stringify(jsonData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `formation-${jsonData.titre || 'export'}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccess('✅ Exporté');
        setTimeout(() => setSuccess(null), 3000);
    };

    const handleImportJson = () => {
        try {
            const parsed = JSON.parse(importJson);
            if (!parsed.titre || !parsed.modules) throw new Error('JSON invalide');
            setJsonData(parsed);
            setShowImportModal(false);
            setImportJson('');
            setImportError(null);
            setSuccess('✅ Importé');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setImportError(err.message);
        }
    };

    // Rendu principal (inchangé)
    return (
        <div className="space-y-4">
            {/* En-tête */}
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-xl font-serif font-bold text-indigo-800 flex items-center gap-2">
                        <FileText size={24} className="text-amber-600" />
                        Constructeur de Formation (Rich)
                    </h2>
                    <p className="text-xs text-indigo-500 font-serif">Modules, leçons avec images, PDF, audio, vidéo, formules, code, texte enrichi</p>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => setShowJsonPreview(!showJsonPreview)} className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg">
                        {showJsonPreview ? '📝 Éditer' : '👁️ JSON'}
                    </button>
                    <button onClick={handleExportJson} className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg">⬇️ Exporter</button>
                    <button onClick={() => setShowImportModal(true)} className="px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-100 border border-indigo-300 rounded-lg">📥 Importer</button>
                    <button onClick={handlePreview} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg flex items-center gap-1"><Eye size={16} /> Démo</button>
                    <button onClick={handleSave} disabled={loading} className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg disabled:opacity-50 flex items-center gap-1">
                        {loading ? '...' : '💾 Sauvegarder'}
                    </button>
                </div>
            </div>

            {!moduleId && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm font-serif flex items-center gap-2">
                    <AlertCircle size={16} />
                    Ce module n'a pas encore d'id en base : créez-le d'abord (formulaire module standard), puis rouvrez ce constructeur avec son <code>moduleId</code> pour activer l'upload de fichiers.
                </div>
            )}

            {error && <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-rose-600 text-sm font-serif whitespace-pre-wrap">{error}</div>}
            {success && <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-600 text-sm font-serif">{success}</div>}

            {showJsonPreview ? (
                <div className="border border-indigo-300 rounded-lg overflow-hidden">
                    <div className="bg-indigo-800 text-white px-4 py-2 flex justify-between items-center">
                        <span className="text-sm font-serif">JSON généré</span>
                        <button onClick={() => { navigator.clipboard.writeText(JSON.stringify(jsonData, null, 2)); setSuccess('✅ Copié'); setTimeout(() => setSuccess(null), 2000); }} className="text-xs text-amber-200 hover:text-white">📋 Copier</button>
                    </div>
                    <pre className="p-4 bg-gray-900 text-green-400 text-xs font-mono overflow-x-auto max-h-96">{JSON.stringify(jsonData, null, 2)}</pre>
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Infos générales */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <h3 className="text-lg font-serif font-bold text-indigo-800 mb-3">📋 Informations</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-xs text-indigo-700 font-serif">ID</label><input type="text" value={jsonData.id || ''} onChange={(e) => setJsonData(prev => ({ ...prev, id: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Titre *</label><input type="text" value={jsonData.titre || ''} onChange={(e) => setJsonData(prev => ({ ...prev, titre: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div className="col-span-2"><label className="block text-xs text-indigo-700 font-serif">Description</label><textarea value={jsonData.description || ''} onChange={(e) => setJsonData(prev => ({ ...prev, description: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" rows="2" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Catégorie</label><input type="text" value={jsonData.categorie || ''} onChange={(e) => setJsonData(prev => ({ ...prev, categorie: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Niveau</label><input type="text" value={jsonData.niveau || ''} onChange={(e) => setJsonData(prev => ({ ...prev, niveau: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Durée estimée</label><input type="text" value={jsonData.dureeEstimee || ''} onChange={(e) => setJsonData(prev => ({ ...prev, dureeEstimee: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Langue</label><input type="text" value={jsonData.langue || 'fr'} onChange={(e) => setJsonData(prev => ({ ...prev, langue: e.target.value }))} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                        </div>
                    </div>

                    {/* Modules */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-lg font-serif font-bold text-indigo-800">📚 Modules ({jsonData.modules?.length || 0})</h3>
                            <button onClick={addModule} className="px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-lg flex items-center gap-1"><Plus size={16} /> Ajouter</button>
                        </div>
                        {jsonData.modules?.length === 0 && <div className="text-center py-6 text-indigo-400 font-serif">Aucun module. Cliquez sur "Ajouter".</div>}
                        {jsonData.modules?.map((module, modIndex) => (
                            <div key={module.id} className="border border-indigo-200 rounded-lg mb-3 overflow-hidden">
                                <div className="bg-amber-50 px-4 py-2 flex items-center justify-between cursor-pointer hover:bg-amber-100" onClick={() => toggleModuleExpand(module.id)}>
                                    <div className="flex items-center gap-3">
                                        <Layers size={16} className="text-amber-700" />
                                        <span className="font-serif font-semibold text-indigo-800">Module {module.ordre || modIndex + 1}: {module.titre || 'Sans titre'}</span>
                                        <span className="text-xs text-indigo-500 font-serif">{module.lecons?.length || 0} leçons</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button onClick={(e) => { e.stopPropagation(); removeModule(module.id); }} className="p-1 text-rose-500 hover:text-rose-700 hover:bg-rose-50 rounded-lg"><Trash2 size={14} /></button>
                                        {expandedModules[module.id] ? <ChevronDown size={16} className="text-indigo-500" /> : <ChevronRight size={16} className="text-indigo-500" />}
                                    </div>
                                </div>
                                {expandedModules[module.id] && (
                                    <div className="p-4 space-y-3">
                                        <div className="grid grid-cols-2 gap-2">
                                            <div><label className="block text-xs text-indigo-700 font-serif">Ordre</label><input type="number" value={module.ordre || modIndex + 1} onChange={(e) => updateModule(module.id, 'ordre', parseInt(e.target.value) || 0)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                            <div><label className="block text-xs text-indigo-700 font-serif">Titre *</label><input type="text" value={module.titre || ''} onChange={(e) => updateModule(module.id, 'titre', e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                            <div className="col-span-2"><label className="block text-xs text-indigo-700 font-serif">Description</label><textarea value={module.description || ''} onChange={(e) => updateModule(module.id, 'description', e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" rows="2" /></div>
                                        </div>
                                        <div className="border-t border-indigo-100 pt-2">
                                            <div className="flex items-center justify-between mb-2">
                                                <h4 className="text-sm font-serif font-semibold text-indigo-700">📖 Leçons ({module.lecons?.length || 0})</h4>
                                                <button onClick={() => addLecon(module.id)} className="px-2 py-1 text-xs font-medium text-indigo-700 hover:bg-indigo-50 border border-indigo-300 rounded-lg flex items-center gap-1"><Plus size={12} /> Ajouter</button>
                                            </div>
                                            {module.lecons?.map((lecon, lecIndex) => (
                                                <div key={lecon.id} className="border border-indigo-200 rounded-lg mb-2 overflow-hidden">
                                                    <div className="bg-indigo-50 px-3 py-1.5 flex items-center justify-between cursor-pointer hover:bg-indigo-100" onClick={() => toggleLeconExpand(lecon.id)}>
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-lg">{getContentTypeIcon(lecon.typeContenu)}</span>
                                                            <span className="text-sm font-serif font-semibold text-indigo-800">Leçon {lecon.ordre || lecIndex + 1}: {lecon.titre || 'Sans titre'}</span>
                                                            <span className="text-xs text-indigo-500">{getContentTypeLabel(lecon.typeContenu)}</span>
                                                        </div>
                                                        <div className="flex items-center gap-2">
                                                            <button onClick={(e) => { e.stopPropagation(); removeLecon(module.id, lecon.id); }} className="p-0.5 text-rose-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg"><Trash2 size={12} /></button>
                                                            {expandedLecons[lecon.id] ? <ChevronDown size={14} className="text-indigo-500" /> : <ChevronRight size={14} className="text-indigo-500" />}
                                                        </div>
                                                    </div>
                                                    {expandedLecons[lecon.id] && (
                                                        <div className="p-3 space-y-2">
                                                            <div className="grid grid-cols-2 gap-2">
                                                                <div><label className="block text-xs text-indigo-700 font-serif">Ordre</label><input type="number" value={lecon.ordre || lecIndex + 1} onChange={(e) => updateLecon(module.id, lecon.id, 'ordre', parseInt(e.target.value) || 0)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                                                <div><label className="block text-xs text-indigo-700 font-serif">Titre *</label><input type="text" value={lecon.titre || ''} onChange={(e) => updateLecon(module.id, lecon.id, 'titre', e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                                            </div>
                                                            <div>
                                                                <label className="block text-xs text-indigo-700 font-serif">Type de contenu</label>
                                                                <select value={lecon.typeContenu || 'IMAGE'} onChange={(e) => changeLeconType(module.id, lecon.id, e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm">
                                                                    {CONTENT_TYPES.map(type => <option key={type.value} value={type.value}>{type.icon} {type.label}</option>)}
                                                                </select>
                                                            </div>
                                                            {renderLeconContentForm(module.id, lecon.id, lecon.typeContenu, lecon.contenu || {})}
                                                            <div className="border-t border-indigo-100 pt-2">
                                                                <h5 className="text-xs font-serif font-semibold text-indigo-600 mb-1">📝 Résumé de la leçon</h5>
                                                                <div className="grid grid-cols-2 gap-2">
                                                                    <div><label className="block text-xs text-indigo-600 font-serif">Titre du résumé</label><input type="text" value={lecon.resumeLecon?.titreResume || ''} onChange={(e) => updateLeconResume(module.id, lecon.id, 'titreResume', e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                                                    <div><label className="block text-xs text-indigo-600 font-serif">À retenir</label><input type="text" value={lecon.resumeLecon?.aRetenir || ''} onChange={(e) => updateLeconResume(module.id, lecon.id, 'aRetenir', e.target.value)} className="w-full px-3 py-1 border border-indigo-300 rounded-lg text-sm" /></div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                            {module.lecons?.length === 0 && <div className="text-center py-2 text-xs text-indigo-400 font-serif">Aucune leçon.</div>}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Fin formation */}
                    <div className="border border-indigo-200 rounded-lg bg-white p-4 shadow-sm">
                        <h3 className="text-lg font-serif font-bold text-indigo-800 mb-3">🏁 Fin de la formation</h3>
                        <div className="grid grid-cols-2 gap-3">
                            <div><label className="block text-xs text-indigo-700 font-serif">Titre de fin</label><input type="text" value={jsonData.FinFormation?.titreResume || ''} onChange={(e) => updateFinFormation('titreResume', e.target.value)} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                            <div><label className="block text-xs text-indigo-700 font-serif">Message de fin</label><input type="text" value={jsonData.FinFormation?.aRetenir || ''} onChange={(e) => updateFinFormation('aRetenir', e.target.value)} className="w-full px-3 py-1.5 border border-indigo-300 rounded-lg text-sm" /></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modale d'import */}
            {showImportModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6">
                        <div className="flex justify-between items-start mb-4">
                            <h3 className="text-lg font-serif font-bold text-indigo-800">Importer un JSON</h3>
                            <button onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }} className="p-1 text-gray-400 hover:text-gray-600">✕</button>
                        </div>
                        <textarea value={importJson} onChange={(e) => setImportJson(e.target.value)} placeholder="Collez le JSON..." className="w-full h-64 px-3 py-2 border border-indigo-300 rounded-lg font-mono text-sm" />
                        {importError && <div className="mt-2 text-rose-600 text-sm bg-rose-50 p-2 rounded-lg">❌ {importError}</div>}
                        <div className="mt-4 flex justify-end gap-2">
                            <button onClick={() => { setShowImportModal(false); setImportError(null); setImportJson(''); }} className="px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-50 border border-indigo-300 rounded-lg">Annuler</button>
                            <button onClick={handleImportJson} className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium hover:bg-amber-700">Valider</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}